// "Risk assessments in this manual" on a manual's page (fixes-1 part 4): an approved manual's own file is read by the AI (no second
// upload) - it finds the risk-assessment section(s) itself, even in a long manual or a scanned table - and "Create RA from this
// manual" makes Draft RAs from them in the RA standard (scores suggested, to review). Shown to a login with Edit on Risk
// Assessments; what was found (manual_ra_scans, sql/fixes-1.sql) is shown to anyone who may see the manual.
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const box = { border: '1px solid var(--line, var(--border))', borderRadius: 10, padding: 14, marginBottom: 14, background: 'var(--card, var(--hull))' }
const h = { fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }
export const pagesLabel = s => (s.from === s.to ? 'p. ' + s.from : 'pp. ' + s.from + '-' + s.to)

// the server reads / drafts (api/ai.js manual_ra, manual_ra_scan); the app's fetch adds the login
export async function manualRaCall(action, manualId) {
  const r = await fetch('/api/ai', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, manual_id: manualId }) })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(j.error || 'The manual could not be read (' + r.status + ')')
  return j
}
function openRa(id) {
  try { localStorage.setItem('hc_open_ra', String(id)) } catch (e) {}
  window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: 'risk_assessments' } }))
}

export default function ManualRaBox({ manual, clientId }) {
  const [riskLvl, setRiskLvl] = useState(null)
  const [scan, setScan] = useState(undefined)   // undefined = loading, null = not read yet / no table
  const [busy, setBusy] = useState(false)
  const [out, setOut] = useState(null)
  const [err, setErr] = useState('')
  const ver = Number(manual.version) || 1
  const usable = manual.status === 'approved' && !manual.archived_at && !!manual.file_url
  useEffect(() => {
    let live = true
    supabase.rpc('my_perm', { p_client: String(clientId), p_module: 'risk' }).then(({ data }) => { if (live) setRiskLvl(Number(data) || 0) }, () => { if (live) setRiskLvl(0) })
    supabase.from('manual_ra_scans').select('version,pages,read_as,sections,has_ra,drafted_at').eq('manual_id', manual.id).maybeSingle()
      .then(({ data, error }) => { if (live) setScan(error ? null : (data && Number(data.version) === ver ? data : null)) }, () => { if (live) setScan(null) })
    return () => { live = false }
  }, [manual.id, clientId, ver])
  if (!usable || riskLvl == null || scan === undefined) return null
  const canMake = riskLvl >= 2
  const ras = scan ? (scan.sections || []).filter(s => s.kind === 'ra') : []
  if (!canMake && !ras.length) return null
  const make = async () => {
    setBusy(true); setErr(''); setOut(null)
    try {
      const j = await manualRaCall('manual_ra', manual.id)
      setOut(j)
      const { data } = await supabase.from('manual_ra_scans').select('version,pages,read_as,sections,has_ra,drafted_at').eq('manual_id', manual.id).maybeSingle()
      if (data) setScan(data)
    } catch (e) { setErr(e.message || String(e)) }
    setBusy(false)
  }
  return (
    <div style={box} className="man-ra">
      <div style={h}>Risk assessments in this manual</div>
      {scan && scan.read_as !== 'error'
        ? (ras.length
          ? <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 13 }}>{ras.map((s, i) => <li key={i}>{s.title} <span style={{ color: 'var(--muted)' }}>{'·'} {pagesLabel(s)}{s.scanned ? ' · scanned table, read as an image' : ''}</span></li>)}</ul>
          : <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>{scan.read_as === 'none' ? 'Only a PDF manual is read for risk assessments.' : 'No risk assessment found in this manual' + (scan.pages ? ' (' + scan.pages + ' pages read' + (scan.read_as === 'text' ? ', text only - over 100 pages' : '') + ')' : '') + '.'}</div>)
        : <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>{scan && scan.read_as === 'error' ? 'The last read of this manual failed - try again.' : 'Not read yet. The AI reads this manual’s own file (scanned pages too) and finds its risk assessment sections.'}</div>}
      {canMake && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={make}>{busy ? 'Reading the manual…' : 'Create RA from this manual'}</button>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Draft risk assessments in the RA standard; the scores are the AI{'’'}s suggestions, marked to review. {busy ? 'This can take a minute or two.' : ''}</span>
      </div>}
      {err && <div role="alert" style={{ marginTop: 8, fontSize: 13, color: 'var(--red)' }}>{err}</div>}
      {out && (
        <div style={{ marginTop: 10, fontSize: 13 }} role="status">
          <div>{out.note}</div>
          {(out.created || []).length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{out.created.map(c => (
            <li key={c.id || c.ra_number}><button type="button" className="ra-link" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--blue, #2563eb)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }} onClick={() => c.id && openRa(c.id)}>{c.ra_number} {c.activity}</button>
              <span style={{ color: 'var(--muted)' }}> {'·'} {c.hazards} hazards {'·'} {c.pages}{c.scanned ? ' (scanned)' : ''}{c.linked ? '' : ' · not linked here (needs Manuals View)'}</span></li>))}</ul>}
          {(out.failed || []).length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--red)' }}>{out.failed.map((f, i) => <li key={i}>{f.title} ({f.pages}): {f.error}</li>)}</ul>}
        </div>
      )}
    </div>
  )
}
